import { useCallback, useEffect, useState } from 'react';
import {
  Card, Group, Text, Badge, Table, Button, ActionIcon, Tooltip, Stack, TextInput,
  Modal, Select, NumberInput, Switch, CopyButton, Code, Box, Transition,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconPlus, IconPencil, IconKey, IconPlayerPlay, IconPlayerPause,
  IconSearch, IconCopy, IconCheck, IconRefresh,
} from '@tabler/icons-react';
import { useArchbaseRemoteServiceApi } from '@archbase/data';
import { RouteXApiClientService } from '@services/ApiClientService';
import { API_TYPE } from '@ioc/IOCTypes';
import type { ApiClientDto, ApiClientCreateResponse } from '@domain/apiClient/ApiClientDto';

export default function ApiClientsView() {
  const [clients, setClients] = useState<ApiClientDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpened, { open: openForm, close: closeForm }] = useDisclosure(false);
  const [keyModal, setKeyModal] = useState<{ open: boolean; apiKey: string }>({ open: false, apiKey: '' });
  const [editingClient, setEditingClient] = useState<ApiClientDto | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formEnv, setFormEnv] = useState('development');
  const [formRate, setFormRate] = useState<number>(60);
  const [formQuota, setFormQuota] = useState<number>(50000);
  const [formProfile, setFormProfile] = useState('car');

  const service = useArchbaseRemoteServiceApi<RouteXApiClientService>(API_TYPE.RouteXApiClient);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const page = await service.findAll(0, 100);
      setClients(page?.content ?? []);
    } catch (e: any) {
      notifications.show({ title: 'Erro', message: e.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const resetForm = () => {
    setFormName(''); setFormDesc(''); setFormEnv('development');
    setFormRate(60); setFormQuota(50000); setFormProfile('car');
    setEditingClient(null);
  };

  const handleCreate = async () => {
    try {
      const result: ApiClientCreateResponse = await service.create({
        name: formName, description: formDesc, environment: formEnv,
        rateLimit: formRate, monthlyQuota: formQuota, defaultProfile: formProfile,
      });
      setKeyModal({ open: true, apiKey: result.apiKey });
      closeForm(); resetForm(); fetchClients();
      notifications.show({ title: 'Sucesso', message: `Client "${result.name}" criado`, color: 'teal' });
    } catch (e: any) {
      notifications.show({ title: 'Erro', message: e.message, color: 'red' });
    }
  };

  const handleUpdate = async () => {
    if (!editingClient?.id) return;
    try {
      await service.update(editingClient.id, {
        name: formName, description: formDesc, environment: formEnv,
        rateLimit: formRate, monthlyQuota: formQuota, defaultProfile: formProfile,
      });
      closeForm(); resetForm(); fetchClients();
      notifications.show({ title: 'Sucesso', message: 'Client atualizado', color: 'teal' });
    } catch (e: any) {
      notifications.show({ title: 'Erro', message: e.message, color: 'red' });
    }
  };

  const handleToggleActive = async (client: ApiClientDto) => {
    try {
      if (client.active) {
        await service.deactivate(client.id!);
      } else {
        await service.activate(client.id!);
      }
      fetchClients();
    } catch (e: any) {
      notifications.show({ title: 'Erro', message: e.message, color: 'red' });
    }
  };

  const handleRegenKey = async (client: ApiClientDto) => {
    try {
      const result = await service.regenerateKey(client.id!);
      setKeyModal({ open: true, apiKey: result.apiKey });
      notifications.show({ title: 'Sucesso', message: 'Nova API Key gerada', color: 'teal' });
    } catch (e: any) {
      notifications.show({ title: 'Erro', message: e.message, color: 'red' });
    }
  };

  const openEdit = (client: ApiClientDto) => {
    setEditingClient(client);
    setFormName(client.name); setFormDesc(client.description ?? '');
    setFormEnv(client.environment ?? 'development');
    setFormRate(client.rateLimit ?? 60); setFormQuota(client.monthlyQuota ?? 50000);
    setFormProfile(client.defaultProfile ?? 'car');
    openForm();
  };

  return (
    <Box p="xl" style={{ height: '100%' }}>
      <Stack gap="xl">
        <Group justify="space-between">
          <Stack gap={2}>
            <Text size="xl" fw={700} style={{ letterSpacing: '-0.02em' }}>API Clients</Text>
            <Text size="sm" c="dimmed">Gerenciar tenants e chaves de acesso</Text>
          </Stack>
          <Group>
            <Tooltip label="Atualizar"><ActionIcon variant="subtle" onClick={fetchClients}><IconRefresh size={18} /></ActionIcon></Tooltip>
            <Button leftSection={<IconPlus size={16} />} onClick={() => { resetForm(); openForm(); }}>Novo Client</Button>
          </Group>
        </Group>

        <Card padding="lg" radius="sm" withBorder>
        <Table.ScrollContainer minWidth={700}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nome</Table.Th>
                <Table.Th>Ambiente</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th ta="center">Rate Limit</Table.Th>
                <Table.Th ta="center">Quota/mes</Table.Th>
                <Table.Th ta="center">Profile</Table.Th>
                <Table.Th ta="right">Ações</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {clients.map((c) => (
                <Table.Tr key={c.id}>
                  <Table.Td>
                    <Stack gap={0}>
                      <Text size="sm" fw={600}>{c.name}</Text>
                      {c.description && <Text size="xs" c="dimmed" lineClamp={1}>{c.description}</Text>}
                    </Stack>
                  </Table.Td>
                  <Table.Td><Badge size="xs" variant="light" color={c.environment === 'production' ? 'red' : 'blue'}>{c.environment}</Badge></Table.Td>
                  <Table.Td><Badge size="xs" variant="dot" color={c.active ? 'teal' : 'gray'}>{c.active ? 'Ativo' : 'Inativo'}</Badge></Table.Td>
                  <Table.Td ta="center"><Text size="xs" ff="monospace">{c.rateLimit}/min</Text></Table.Td>
                  <Table.Td ta="center"><Text size="xs" ff="monospace">{c.monthlyQuota?.toLocaleString()}</Text></Table.Td>
                  <Table.Td ta="center"><Badge size="xs" variant="outline">{c.defaultProfile}</Badge></Table.Td>
                  <Table.Td ta="right">
                    <Group gap={4} justify="flex-end">
                      <Tooltip label="Editar"><ActionIcon variant="subtle" size="sm" onClick={() => openEdit(c)}><IconPencil size={14} /></ActionIcon></Tooltip>
                      <Tooltip label="Regenerar Key"><ActionIcon variant="subtle" size="sm" color="orange" onClick={() => handleRegenKey(c)}><IconKey size={14} /></ActionIcon></Tooltip>
                      <Tooltip label={c.active ? 'Desativar' : 'Ativar'}>
                        <ActionIcon variant="subtle" size="sm" color={c.active ? 'red' : 'teal'} onClick={() => handleToggleActive(c)}>
                          {c.active ? <IconPlayerPause size={14} /> : <IconPlayerPlay size={14} />}
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
              {clients.length === 0 && !loading && (
                <Table.Tr><Table.Td colSpan={7}><Text ta="center" c="dimmed" py="xl">Nenhum client cadastrado</Text></Table.Td></Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        </Card>
      </Stack>

      {/* Create/Edit Modal */}
      <Modal opened={formOpened} onClose={() => { closeForm(); resetForm(); }} title={editingClient ? 'Editar Client' : 'Novo API Client'} size="md">
        <Stack gap="md">
          <TextInput label="Nome" required value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="ex: meu-app" />
          <TextInput label="Descrição" value={formDesc} onChange={(e) => setFormDesc(e.target.value)} placeholder="Descrição do client" />
          <Select label="Ambiente" data={[{ value: 'development', label: 'Development' }, { value: 'staging', label: 'Staging' }, { value: 'production', label: 'Production' }]} value={formEnv} onChange={(v) => setFormEnv(v ?? 'development')} />
          <Group grow>
            <NumberInput label="Rate Limit (req/min)" value={formRate} onChange={(v) => setFormRate(Number(v))} min={1} max={10000} />
            <NumberInput label="Quota Mensal" value={formQuota} onChange={(v) => setFormQuota(Number(v))} min={100} max={10000000} step={1000} />
          </Group>
          <Select label="Profile Padrão" data={[{ value: 'car', label: 'Carro' }, { value: 'truck', label: 'Caminhão' }]} value={formProfile} onChange={(v) => setFormProfile(v ?? 'car')} />
          <Button fullWidth mt="sm" onClick={editingClient ? handleUpdate : handleCreate}>
            {editingClient ? 'Salvar' : 'Criar Client'}
          </Button>
        </Stack>
      </Modal>

      {/* API Key Display Modal */}
      <Modal opened={keyModal.open} onClose={() => setKeyModal({ open: false, apiKey: '' })} title="API Key Gerada" size="md">
        <Stack gap="md">
          <Text size="sm" c="red" fw={500}>Guarde esta chave com segurança. Ela não será mostrada novamente.</Text>
          <Code block style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>{keyModal.apiKey}</Code>
          <CopyButton value={keyModal.apiKey}>
            {({ copied, copy }) => (
              <Button variant="light" fullWidth leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />} color={copied ? 'teal' : 'blue'} onClick={copy}>
                {copied ? 'Copiado!' : 'Copiar API Key'}
              </Button>
            )}
          </CopyButton>
        </Stack>
      </Modal>
    </Box>
  );
}

